import { z } from 'zod';
import {
  withPaginationParams,
  withSortableParams,
  isMongoId,
} from '../../helpers';
import {
  ActionRef,
  ActionTransactionSubTypes,
  ActionTypes,
  ActionCategory,
} from 'further-types/lp/action';
import {
  CurrencyCode,
  ForeignCurrencyCostRateCalculationType,
  FxDataFeed,
  VehicleJurisdiction,
  CommitmentType,
  CapitalContributionRule,
} from 'further-types/lp/vehicle';
import {
  FeeApportionmentBasis,
  LpFeeStartDate,
  LpArrearsCalculation,
  LpFeeType,
  LpFeeCalculationBasis,
  LpAccrualFrequency,
  LpFeeCommitmentStatus,
} from 'further-types/lp/lp-fee-template';
import { AllocationRule } from 'further-types/lp/allocations';
import { FeesEqualisationTreatment } from 'further-types/lp/equalisation';
import {
  CarriedInterestDayCountFormat,
  PreferredReturnAccrualMethod,
  PreferredReturnBasis,
  PreferredReturnCompoundingPeriod,
} from 'further-types/lp/carry-settings';

export const ListPaginated = withPaginationParams(
  z.object({
    search: z.string().optional().nullable(),
  }),
  ['vehicleName', 'baseCurrency'],
);

export const ListAll = withSortableParams(z.object({}), [
  'vehicleName',
  'baseCurrency',
]);

export const Create = z.object({
  vehicleName: z.string().min(1, 'Please enter vehicle name.'),
});

export const Update = Create.extend({
  baseCurrency: z.nativeEnum(CurrencyCode),
  fxDataFeed: z.nativeEnum(FxDataFeed),
  defaultAllocationRule: z.nativeEnum(AllocationRule),
  foreignCurrencyCostRateCalculation: z.nativeEnum(
    ForeignCurrencyCostRateCalculationType,
  ),
  financialYearEndDay: z
    .number({ required_error: 'Please select a day.' })
    .min(1)
    .max(31),
  financialYearEndMonth: z
    .number({ required_error: 'Please select a month.' })
    .min(1)
    .max(12),
  vehicleJurisdiction: z.nativeEnum(VehicleJurisdiction, {
    required_error: 'Please select a vehicle jurisdiction.',
  }),
  commitmentType: z.nativeEnum(CommitmentType, {
    required_error: 'Please select a commitment type.',
  }),
  capitalContributionRule: z
    .nativeEnum(CapitalContributionRule)
    .optional()
    .nullable(),
  capitalContributionPercentage: z
    .number()
    .min(0)
    .max(100)
    .optional()
    .nullable(),
  fixedCapitalAmount: z.number().min(0).optional().nullable(),
})
  .refine(
    (data) => {
      if (data.commitmentType === CommitmentType.CapitalAndLoan) {
        return (
          data.capitalContributionRule !== null &&
          data.capitalContributionRule !== undefined
        );
      }
      return true;
    },
    {
      message: 'Please select a capital contribution rule.',
      path: ['capitalContributionRule'],
    },
  )
  .refine(
    (data) => {
      if (
        data.commitmentType === CommitmentType.CapitalAndLoan &&
        data.capitalContributionRule ===
          CapitalContributionRule.FixedCommitmentPercentage
      ) {
        return (
          data.capitalContributionPercentage !== null &&
          data.capitalContributionPercentage !== undefined
        );
      }
      return true;
    },
    {
      message: 'Please enter a capital contribution percentage.',
      path: ['capitalContributionPercentage'],
    },
  )
  .refine(
    (data) => {
      if (
        data.commitmentType === CommitmentType.CapitalAndLoan &&
        data.capitalContributionRule ===
          CapitalContributionRule.ProRataOfFixedPartnershipCapital
      ) {
        return (
          data.fixedCapitalAmount !== null &&
          data.fixedCapitalAmount !== undefined
        );
      }
      return true;
    },
    {
      message: 'Please enter a fixed capital amount.',
      path: ['fixedCapitalAmount'],
    },
  )
  .refine(
    (data) => {
      const day = data.financialYearEndDay;
      const month = data.financialYearEndMonth;

      if (day === undefined || month === undefined) return true;

      // Months with 30 days
      const monthsWith30Days = [4, 6, 9, 11];
      // February
      if (month === 2 && day > 28) {
        return false;
      }
      // Months with 30 days
      if (monthsWith30Days.includes(month) && day > 30) {
        return false;
      }

      return true;
    },
    {
      message: 'Invalid date. Please check the day for the selected month.',
      path: ['financialYearEndDay'],
    },
  );

export const UpdateAccountMappings = z.object({
  accountMappings: z.array(
    z.object({
      customActionTypeId: z.string().optional().nullable(),
      actionCategory: z.nativeEnum(ActionCategory).optional().nullable(),
      creditTransactionTypeId: z
        .string()
        .nullable()
        .refine(isMongoId({ optional: true })),
      debitTransactionTypeId: z
        .string()
        .nullable()
        .refine(isMongoId({ optional: true })),
      actionRef: z.nativeEnum(ActionRef),
      customActionType: z.string().optional().nullable(),
      actionType: z.nativeEnum(ActionTypes),
      transactionSubType: z.nativeEnum(ActionTransactionSubTypes),
    }),
  ),
});

const UpdateFeeSettingsCoreFields = z.object({
  hasApprovedFees: z.boolean().optional(),
  hasCommitments: z.boolean().optional(),
  _id: z.string().optional(),
  name: z.string().min(1, 'Please enter a name.'),
  feeApportionmentBasis: z.nativeEnum(FeeApportionmentBasis, {
    message: 'Please select a fee apportionment basis.',
  }),
  feeStartDate: z.nativeEnum(LpFeeStartDate, {
    message: 'Please select a fee start date.',
  }),
  arrearsCalculation: z.nativeEnum(LpArrearsCalculation, {
    message: 'Please select an arrears calculation.',
  }),
  isDefaultFeeTemplate: z.boolean(),
});

const UpdateFeeSettingsFeeTemplateFeesFields = z.array(
  z
    .discriminatedUnion('type', [
      z.object({
        type: z.literal(LpFeeType.OneOffFixed),
        name: z.string().min(1, { message: 'Please enter a name.' }),
        commitmentStatus: z.nativeEnum(LpFeeCommitmentStatus, {
          message: 'Please select a commitment status.',
        }),
        feeExTax: z.number().min(1, { message: 'Please enter a fee amount.' }),
        salesTaxRate: z.number(),
      }),
      z.object({
        type: z.literal(LpFeeType.RecurringPercentage),
        name: z.string().min(1, { message: 'Please enter a name.' }),
        calculationBasis: z.nativeEnum(LpFeeCalculationBasis, {
          message: 'Please select a calculation basis.',
        }),
        accrualFrequency: z.nativeEnum(LpAccrualFrequency, {
          message: 'Please select an accrual frequency.',
        }),
        startPeriod: z
          .number()
          .min(1, { message: 'Start period must be greater than 0.' }),
        endPeriod: z
          .number()
          .min(1, { message: 'End period must be greater than 0.' }),
        commitmentStatus: z.nativeEnum(LpFeeCommitmentStatus, {
          message: 'Please select a commitment status.',
        }),
        feeExTax: z.number().min(1, { message: 'Please enter a fee amount.' }),
        salesTaxRate: z.number(),
      }),
    ])
    .refine(
      (fee) => {
        if (fee.type === LpFeeType.OneOffFixed) return true;
        return fee.endPeriod >= fee.startPeriod;
      },
      {
        message: 'End period must be greater than start period.',
        path: ['endPeriod'],
      },
    ),
);

export const UpdateFeeSettings = z.object({
  lpFeeTemplates: z.array(
    z.discriminatedUnion('feesEqualisationTreatment', [
      z.object({
        ...UpdateFeeSettingsCoreFields.shape,
        feesEqualisationTreatment: z.literal(FeesEqualisationTreatment.Full),
        lpFeeTemplateFees: UpdateFeeSettingsFeeTemplateFeesFields,
      }),
      z.object({
        ...UpdateFeeSettingsCoreFields.shape,
        feesEqualisationTreatment: z.literal(FeesEqualisationTreatment.Partial),
        feesEqualisationTreatmentPercentage: z
          .number()
          .min(0.01, 'Percentage must be greater than 0')
          .max(100, 'Percentage must be between 0 and 100'),
        lpFeeTemplateFees: UpdateFeeSettingsFeeTemplateFeesFields,
      }),
      z.object({
        ...UpdateFeeSettingsCoreFields.shape,
        feesEqualisationTreatment: z.literal(FeesEqualisationTreatment.None),
        lpFeeTemplateFees: UpdateFeeSettingsFeeTemplateFeesFields,
      }),
    ]),
  ),
});

export const UpdateEqualisationInterestSettings = z.object({
  dayCount: z.nativeEnum(CarriedInterestDayCountFormat),
  equalisationInterestFeeTemplates: z.array(
    z
      .object({
        _id: z.string().optional(),
        name: z.string().min(1),
        equalisationInterestRate: z.number().min(0),
        equalisationInterestAccrualMethod: z.nativeEnum(
          PreferredReturnAccrualMethod,
        ),
        equalisationInterestCompoundingPeriod: z
          .nativeEnum(PreferredReturnCompoundingPeriod)
          .nullable(),
        equalisationInterestBasis: z.nativeEnum(PreferredReturnBasis),
        isDefault: z.boolean(),
        isInUse: z.boolean().optional(),
      })
      .refine(
        (data) => {
          if (
            data.equalisationInterestAccrualMethod ===
            PreferredReturnAccrualMethod.Compound
          ) {
            return data.equalisationInterestCompoundingPeriod !== null;
          }
          return true;
        },
        {
          message:
            'Compounding period is required when accrual method is Compound',
          path: ['equalisationInterestCompoundingPeriod'],
        },
      ),
  ),
});
